/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { getAuthToken } from "@services/auth/auth";
import { UserManager } from "oidc-client-ts";
import { globalErrorType } from "./ErrorContext";
import { getRequiredEnvVar } from "@shared/utils/validate-env";
import { getRealLocalStorage } from "@/shared/utils/storage/localStorageWrapper";
import { resolveOidcRedirectUri } from "@shared/utils/native-oidc";

/**
 * Creates UserManager instance for sign up operations.
 *
 * OIDC env vars are read at CALL time (not module load) and gated on
 * `isMockAuthEnabled()` so that under isolate:false a sibling suite that froze
 * FF_MOCK_AUTH=true cannot force an eager getRequiredEnvVar() throw for a later
 * real-auth suite.
 *
 * @returns {UserManager} Configured UserManager instance
 */
const createUserManager = () => {
  return new UserManager({
    authority: getRequiredEnvVar('REACT_APP_AUTHORITY_URL'),
    client_id: getRequiredEnvVar('REACT_APP_CLIENT_ID'),
    redirect_uri: resolveOidcRedirectUri(),
    response_type: "code",
    scope: "openid",
  });
};

/**
 * Saves return path if in app context.
 * 
 * @returns {void}
 */
export function saveReturnPathIfApp(): void {
  const isApp = (window.location?.pathname ?? "").startsWith("/app");
  if (isApp) {
    try {
      getRealLocalStorage().setItem("returnPath", window.location.href);
    } catch (error) {
      console.warn('Failed to save return path:', error);
    }
  }
}

/**
 * Validates token presence.
 * 
 * @returns {Promise<boolean>} True if token exists
 */
export async function validateToken(): Promise<boolean> {
  const access_token = getAuthToken();
  return !!access_token;
}

/**
 * Handles sign up flow.
 * 
 * @param {Function} setGlobalError - Error setter
 * @returns {Promise<void>}
 */
export async function signUp(setGlobalError: (error: globalErrorType) => void): Promise<void> {
  saveReturnPathIfApp();
  try {
    const userManager = createUserManager();
    await userManager.removeUser();
    await userManager.signinRedirect();
  } catch (error) {
    setGlobalError(error as globalErrorType);
  }
}

/**
 * Handles token validation result.
 * 
 * @returns {void}
 */
export function handleTokenValidationResult(): void {
  saveReturnPathIfApp();
  // await signUp(); // Commented out for now
}

/**
 * Handles token validation error.
 * 
 * @param {Error} err - Error object
 * @param {Function} setGlobalError - Error setter
 * @returns {Promise<void>}
 */
export async function handleTokenValidationError(
  err: Error,
  setGlobalError: (error: globalErrorType) => void
): Promise<void> {
  saveReturnPathIfApp();
  // Only call signUp if we're in /app path (to redirect to login)
  const isApp = (window.location?.pathname ?? "").startsWith("/app");
  if (isApp) {
    await signUp(setGlobalError);
  }
  setGlobalError(err as globalErrorType);
}
